import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { TrendBand, CHART } from '../components/analytics/kit';
import { useAuth } from '../context/AuthContext';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import VendorDetailsModal from '../components/VendorDetailsModal';

const Orders = () => {
  const { role } = useAuth();
  const isCentre = role === 'CENTRE';   // read-only view for centre admins

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy] = useState('date_desc');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);

  // Address edit states
  const [editingAddressOrder, setEditingAddressOrder] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '',
    phone: '',
    houseNo: '',
    area: '',
    landmark: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const handleStartEditAddress = (order) => {
    const addr = order.deliveryAddress || {};
    setEditingAddressOrder(order);
    setAddressForm({
      name: addr.name || order.userId?.name || '',
      phone: addr.phone || order.userId?.phoneNumber || '',
      houseNo: addr.houseNo || '',
      area: addr.area || '',
      landmark: addr.landmark || '',
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pincode || ''
    });
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    try {
      if (addressForm.name.trim().length < 3) {
        alert("Receiver Name must be at least 3 characters");
        return;
      }
      const cleanPhone = addressForm.phone.replace(/\D/g, '');
      if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
        alert("Please enter a valid 10-digit mobile number starting with 6-9");
        return;
      }
      const cleanPincode = addressForm.pincode.replace(/\D/g, '');
      if (!/^\d{6}$/.test(cleanPincode)) {
        alert("Please enter a valid 6-digit Pincode");
        return;
      }

      const res = await api.put(`/orders/${editingAddressOrder._id}/delivery-address`, {
        ...addressForm,
        phone: cleanPhone,
        pincode: cleanPincode
      });

      if (res.data.success) {
        alert("Order address updated successfully!");
        setIsAddressModalOpen(false);
        setEditingAddressOrder(null);
        fetchOrders();
      }
    } catch (err) {
      alert("Error: " + (err.response?.data?.message || err.message));
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/orders');
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    
    // Socket listener for auto-updates
    const socket = window.socket; // Assuming socket is attached to window globally
    if (socket) {
      socket.on('order_status_updated', (data) => {
        console.log('🔔 Order Status Updated via Webhook:', data);
        setOrders(prev => prev.map(ord => {
          if (ord._id === data.orderId) {
            return {
              ...ord,
              status: data.status,
              awb_code: data.awb || ord.awb_code,
              courier_company_id: data.courier_name || ord.courier_company_id,
              trackingUrl: data.tracking_url || ord.trackingUrl
            };
          }
          return ord;
        }));
      });
      return () => socket.off('order_status_updated');
    }
  }, [fetchOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      const res = await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      if (res.data.success) {
        setOrders(prev => prev.map(ord => ord._id === orderId ? { ...ord, status: newStatus } : ord));
      }
    } catch (error) {
      alert("Failed to update status: " + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Ordered':
      case 'Pending':
        return { background: '#FFF8E1', color: '#F57F17', border: '#FFD700' };
      case 'Notified':
        return { background: '#E0F2F1', color: '#00796B', border: '#80CBC4' };
      case 'Packed':
        return { background: '#E1F5FE', color: '#0288D1', border: '#81D4FA' };
      case 'Shipped':
        return { background: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' };
      case 'Driver Picked':
        return { background: '#EDE7F6', color: '#5E35B1', border: '#B39DDB' };
      case 'Delivered':
      case 'Completed':
        return { background: '#E8F5E9', color: '#2E7D32', border: '#66BB6A' };
      case 'Cancelled':
        return { background: '#FFEBEE', color: '#C62828', border: '#EF5350' };
      default:
        return { background: '#F5F5F5', color: '#616161', border: '#BDBDBD' };
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const generateOrderId = (id) => {
    return `#ORD-${id.slice(-4).toUpperCase()}`;
  };

  // Metrics calculations for Shiprocket/Orders
  const totalShipments = orders.length;
  const pendingBooking = orders.filter(o => !o.shipmentId && !['Cancelled', 'Delivered'].includes(o.status)).length;
  const pendingAWB = orders.filter(o => o.shipmentId && !o.awb_code && !['Cancelled', 'Delivered'].includes(o.status)).length;
  const inTransit = orders.filter(o => o.awb_code && !['Delivered', 'Cancelled'].includes(o.status)).length;
  const deliveredCount = orders.filter(o => o.status === 'Delivered').length;

  const filteredOrders = orders
    .filter(order => {
      const customerName = order.userId?.name || 'Unknown';
      const itemName = order.productName || order.productId?.name || 'Unknown';
      const awb = order.awb_code || '';
      const shipmentId = order.shipmentId || '';

      const matchesSearch =
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        awb.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipmentId.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesStatus = true;
      if (statusFilter === 'pending_booking') {
        matchesStatus = !order.shipmentId && !['Cancelled', 'Delivered'].includes(order.status);
      } else if (statusFilter === 'pending_awb') {
        matchesStatus = !!order.shipmentId && !order.awb_code && !['Cancelled', 'Delivered'].includes(order.status);
      } else if (statusFilter === 'in_transit') {
        matchesStatus = !!order.awb_code && !['Delivered', 'Cancelled'].includes(order.status);
      } else if (statusFilter !== 'all') {
        matchesStatus = order.status === statusFilter;
      }
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'date_asc') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'price_desc') return (b.totalAmount || 0) - (a.totalAmount || 0);
      if (sortBy === 'price_asc') return (a.totalAmount || 0) - (b.totalAmount || 0);
      return 0;
    });

  return (
    <div className="p-8">
      <TrendBand docs={orders} amountKey="totalAmount" title="Order Revenue Flow · Live" accent={CHART.violet} />
      {/* Title Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="font-serif text-3xl font-bold" style={{ color: '#F4EBD8' }}>Order Management</h2>
          <p className="mt-1 text-stone-500 text-sm">Track delivery status and update order progress</p>
        </div>
        
        <button
          onClick={fetchOrders}
          className="bg-white border border-stone-200 text-[#201D42] px-4 py-2 rounded-xl font-bold text-sm hover:bg-stone-100 transition-all flex items-center gap-2 shadow-sm"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
          Sync Orders
        </button>
      </div>

      {/* Shiprocket Logistics Metrics Cards */}
      {!isCentre && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
          <div 
            onClick={() => setStatusFilter('all')}
            className={`bg-white p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${statusFilter === 'all' ? 'border-[#E8B84B] ring-1 ring-[#E8B84B]' : 'border-stone-200/80 hover:border-[#E8B84B]/50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-stone-400 tracking-wider">Total Shipments</span>
              <span className="material-symbols-outlined text-xl text-stone-400">package_2</span>
            </div>
            <div className="text-3xl font-black text-stone-900">{totalShipments}</div>
          </div>

          <div 
            onClick={() => setStatusFilter('pending_booking')}
            className={`bg-white p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${statusFilter === 'pending_booking' ? 'border-[#E8B84B] ring-1 ring-[#E8B84B]' : 'border-stone-200/80 hover:border-[#E8B84B]/50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-[#201D42] tracking-wider">Ready to Book</span>
              <span className="material-symbols-outlined text-xl text-red-500">pending_actions</span>
            </div>
            <div className="text-3xl font-black text-[#201D42]">{pendingBooking}</div>
          </div>

          <div 
            onClick={() => setStatusFilter('pending_awb')}
            className={`bg-white p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${statusFilter === 'pending_awb' ? 'border-[#E8B84B] ring-1 ring-[#E8B84B]' : 'border-stone-200/80 hover:border-[#E8B84B]/50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-orange-600 tracking-wider">AWB Pending</span>
              <span className="material-symbols-outlined text-xl text-orange-500">barcode_scanner</span>
            </div>
            <div className="text-3xl font-black text-orange-600">{pendingAWB}</div>
          </div>

          <div 
            onClick={() => setStatusFilter('in_transit')}
            className={`bg-white p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${statusFilter === 'in_transit' ? 'border-[#E8B84B] ring-1 ring-[#E8B84B]' : 'border-stone-200/80 hover:border-[#E8B84B]/50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-blue-600 tracking-wider">In Transit</span>
              <span className="material-symbols-outlined text-xl text-blue-500">local_shipping</span>
            </div>
            <div className="text-3xl font-black text-blue-600">{inTransit}</div>
          </div>

          <div 
            onClick={() => setStatusFilter('delivered')}
            className={`bg-white p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${statusFilter === 'delivered' ? 'border-[#E8B84B] ring-1 ring-[#E8B84B]' : 'border-stone-200/80 hover:border-[#E8B84B]/50'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase text-emerald-600 tracking-wider">Delivered</span>
              <span className="material-symbols-outlined text-xl text-emerald-500">task_alt</span>
            </div>
            <div className="text-3xl font-black text-emerald-600">{deliveredCount}</div>
          </div>
        </div>
      )}

      {/* Control Filters and Search */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative flex-1 md:max-w-md w-full">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">search</span>
          <input 
            type="text" 
            placeholder="Search by customer, item, AWB or Shipment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B]"
          />
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
          >
            <option value="all">All Status</option>
            <option value="pending_booking">Ready to Book</option>
            <option value="pending_awb">AWB Pending (Packed)</option>
            <option value="in_transit">In Transit (Shipped)</option>
            <option value="Delivered">Delivered</option>
            <option value="Ordered">Ordered</option>
            <option value="Notified">Notified</option>
            <option value="Driver Picked">Driver Picked</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(212,175,55,0.2)', boxShadow: '0 8px 30px rgb(0,0,0,0.04)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-xs" style={{ background: 'rgba(244,235,216,0.05)', borderBottom: '1px solid rgba(212,175,55,0.25)' }}>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>Order ID</th>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>Date</th>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>Customer</th>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>Item</th>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>Amount</th>
              <th className="px-6 py-4 font-bold uppercase tracking-widest" style={{ color: '#F4EBD8' }}>
                {isCentre ? 'Status' : 'Update Status'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-stone-400">Loading orders...</td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-stone-400">No physical product orders yet. Orders from the Divine Marketplace and Centre Shop deliveries will appear here.</td>
              </tr>
            ) : (
              filteredOrders.map((order) => {
                const statusStyle = getStatusStyle(order.status);
                return (
                  <tr key={order._id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-5 font-bold text-stone-700">
                      {generateOrderId(order._id)}
                    </td>
                    <td className="px-6 py-5 text-stone-500 text-sm">{formatDate(order.createdAt)}</td>
                    <td className="px-6 py-5">
                        <div className="flex flex-col">
                            <button
                                onClick={() => {
                                    setSelectedCustomerId(order.userId?._id || order.userId?.id || order.userId);
                                    setIsModalOpen(true);
                                }}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left transition-all text-xs"
                            >
                                {order.userId?.name || 'Guest'}
                            </button>
                            {order.userId?.email && <span className="text-[10px] text-stone-400">{order.userId.email}</span>}
                            
                            {/* Shipping Address Display & Edit Trigger */}
                            <div className="mt-2 text-[10px] text-stone-600 bg-stone-50 p-2 rounded-lg border border-stone-200/60 max-w-[220px]">
                              <div className="font-bold text-stone-700 flex items-center justify-between mb-1">
                                <span>Shipping Address</span>
                                <button
                                  onClick={() => handleStartEditAddress(order)}
                                  className="text-[#201D42] hover:text-black hover:underline text-[9px] font-bold flex items-center gap-0.5"
                                  title="Edit Shipping Address"
                                >
                                  <span className="material-symbols-outlined text-[9px]">edit</span> Edit
                                </button>
                              </div>
                              {order.deliveryAddress ? (
                                <p className="leading-tight font-medium">
                                  {order.deliveryAddress.name}<br />
                                  {order.deliveryAddress.phone}<br />
                                  {order.deliveryAddress.houseNo}, {order.deliveryAddress.area}<br />
                                  {order.deliveryAddress.landmark && `${order.deliveryAddress.landmark}, `}
                                  {order.deliveryAddress.city}, {order.deliveryAddress.state} - {order.deliveryAddress.pincode}
                                </p>
                              ) : (
                                <p className="italic text-stone-400">No snapshot address saved. Click Edit to add one.</p>
                              )}
                            </div>
                        </div>
                    </td>
                    <td className="px-6 py-5">
                        <span className="text-stone-600 text-sm font-medium block mb-1.5">
                          {order.quantity > 1 ? <strong className="text-stone-900 font-black mr-1">{order.quantity}x</strong> : ''}{order.productName || order.productId?.name || 'Product'}
                        </span>
                        {(order.vendorId || order.createdBy) && (
                          <div className="text-[10px] text-stone-500 bg-stone-50 border border-stone-200/50 rounded p-1.5 w-fit">
                            Sold by:{' '}
                            {order.vendorId ? (
                              <button
                                  onClick={() => {
                                      setSelectedVendorId(order.vendorId._id || order.vendorId);
                                      setIsVendorModalOpen(true);
                                  }}
                                  className="font-bold text-[#201D42] hover:underline"
                              >
                                  {order.vendorId.shopName || order.createdBy?.name || 'Vendor Profile'}
                              </button>
                            ) : (
                              <button
                                  onClick={() => {
                                      setSelectedCustomerId(order.createdBy._id || order.createdBy);
                                      setIsModalOpen(true);
                                  }}
                                  className="font-bold text-[#201D42] hover:underline"
                              >
                                  {order.createdBy.name || 'User Profile'}
                              </button>
                            )}
                          </div>
                        )}
                    </td>
                    <td className="px-6 py-5 font-bold" style={{ color: '#F4EBD8' }}>
                      ₹{order.totalAmount || (order.unitPrice * order.quantity) || (order.productId?.price * order.quantity) || 0}
                    </td>
                    <td className="px-6 py-5">
                      {isCentre ? (
                        // ── CENTRE: read-only status badge only ─────────────────
                        <span
                          className="px-3 py-1.5 rounded-lg text-xs font-bold border"
                          style={{
                            background: statusStyle.background,
                            color: statusStyle.color,
                            borderColor: statusStyle.border,
                          }}
                        >
                          {order.status}
                        </span>
                      ) : (
                        // ── ADMIN / VENDOR: full controls ───────────────────────
                        <div className="flex flex-col gap-3 min-w-[240px] bg-stone-50/50 p-3 rounded-xl border border-stone-200/60 my-2">
                          {/* Manual Status Dropdown */}
                          <div className="flex flex-col gap-1">
                            <span className="text-[9px] font-black uppercase text-stone-400 tracking-wider">Internal Status</span>
                            <select
                              disabled={updatingId === order._id}
                              value={order.status}
                              onChange={(e) => handleUpdateStatus(order._id, e.target.value)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-bold border outline-none cursor-pointer transition-all shadow-sm w-full bg-white"
                              style={{
                                color: statusStyle.color,
                                borderColor: statusStyle.border
                              }}
                            >
                              <option value="Ordered">Ordered</option>
                              <option value="Notified">Notified</option>
                              <option value="Packed">Packed</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Driver Picked">Driver Picked</option>
                              <option value="Delivered">Delivered</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>

                          {/* Shiprocket Section Header */}
                          <div className="border-t border-stone-200/80 pt-2 flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black uppercase text-[#201D42] tracking-widest flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">local_shipping</span>
                                Shiprocket Flow
                              </span>
                              {order.status === 'Delivered' && (
                                <span className="text-[9px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded">Completed</span>
                              )}
                            </div>

                            {/* Step 1: Create Shipment */}
                            <div className="flex flex-col gap-1">
                              {!order.shipmentId ? (
                                <button
                                  disabled={updatingId === order._id || ['Delivered', 'Cancelled'].includes(order.status)}
                                  onClick={async () => {
                                    setUpdatingId(order._id);
                                    try {
                                      const res = await api.post(`/orders/${order._id}/create-shipment`);
                                      if (res.data.success) {
                                        alert("🚀 Step 1 Complete: Shipment registered successfully on Shiprocket!");
                                        fetchOrders();
                                      }
                                    } catch (err) {
                                      alert("Shiprocket Error: " + (err.response?.data?.message || err.message));
                                    } finally {
                                      setUpdatingId(null);
                                    }
                                  }}
                                  className="w-full bg-[#201D42] text-white hover:bg-black py-1.5 px-3 rounded-lg font-bold text-[11px] transition-all flex items-center justify-center gap-1.5 shadow-sm active:scale-95 disabled:opacity-40"
                                >
                                  {updatingId === order._id ? (
                                    <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <span className="material-symbols-outlined text-[14px]">add_box</span>
                                      <span>Step 1: Book Shipment</span>
                                    </>
                                  )}
                                </button>
                              ) : (
                                <div className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-100 rounded-lg p-1.5 flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[12px] text-emerald-600">check_circle</span>
                                    <span>Step 1: Registered</span>
                                  </div>
                                  <span className="text-[9px] text-stone-500 font-mono pl-4">ID: {order.shipmentId}</span>
                                </div>
                              )}
                            </div>

                            {/* Step 2: Generate AWB */}
                            {order.shipmentId && (
                              <div className="flex flex-col gap-1">
                                {!order.awb_code ? (
                                  <button
                                    disabled={updatingId === order._id}
                                    onClick={async () => {
                                      setUpdatingId(order._id);
                                      try {
                                        const res = await api.post(`/orders/${order._id}/generate-awb`);
                                        if (res.data.success) {
                                          alert("✅ Step 2 Complete: AWB generated! Shipment is now Shipped.");
                                          fetchOrders();
                                        }
                                      } catch (err) {
                                        alert("AWB Error: " + (err.response?.data?.message || err.message));
                                      } finally {
                                        setUpdatingId(null);
                                      }
                                    }}
                                    className="w-full bg-[#E8B84B] hover:bg-black text-white py-1.5 px-3 rounded-lg font-bold text-[11px] transition-all flex items-center justify-center gap-1.5 shadow-sm active:scale-95 disabled:opacity-40"
                                  >
                                    {updatingId === order._id ? (
                                      <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                    ) : (
                                      <>
                                        <span className="material-symbols-outlined text-[14px]">barcode_scanner</span>
                                        <span>Step 2: Generate AWB</span>
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <div className="text-[10px] text-blue-700 font-semibold bg-blue-50 border border-blue-100 rounded-lg p-1.5 flex flex-col gap-0.5">
                                    <div className="flex items-center gap-1">
                                      <span className="material-symbols-outlined text-[12px] text-blue-600">check_circle</span>
                                      <span>Step 2: AWB Generated</span>
                                    </div>
                                    <span className="text-[9px] text-[#201D42] font-black pl-4">AWB: {order.awb_code}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Step 3: Simulation & Link */}
                            {order.awb_code && (
                              <div className="flex flex-col gap-1.5 border-t border-stone-200/50 pt-1.5">
                                <div className="flex justify-between items-center text-[10px] text-stone-600 font-bold">
                                  <span>Tracking URL</span>
                                  {order.trackingUrl || order.tracking_url ? (
                                    <a
                                      href={order.trackingUrl || order.tracking_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#201D42] hover:underline flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-black"
                                    >
                                      Link
                                      <span className="material-symbols-outlined text-[9px]">open_in_new</span>
                                    </a>
                                  ) : (
                                    <span className="text-[9px] text-stone-400 italic">Pending Courier</span>
                                  )}
                                </div>

                                {order.status !== 'Delivered' && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Simulate Shiprocket "DELIVERED" update for AWB: ${order.awb_code}?`)) {
                                        try {
                                          const res = await api.post(`/webhooks/shiprocket?token=centre_webhook_secret_key`, {
                                            awb: order.awb_code,
                                            current_status: 'DELIVERED'
                                          });
                                          if (res.data.success) {
                                            alert("🚀 Simulated Successful: Order marked Delivered!");
                                            fetchOrders();
                                          }
                                        } catch (err) {
                                          alert("Simulation Failed: " + (err.response?.data?.message || err.message));
                                        }
                                      }
                                    }}
                                    className="w-full border border-stone-200 hover:bg-stone-50 text-stone-600 py-1 px-2 rounded-lg font-bold text-[10px] transition-all flex items-center justify-center gap-1 active:scale-95"
                                  >
                                    <span className="material-symbols-outlined text-[12px] text-green-600">sports_motorsports</span>
                                    <span>Simulate Delivery</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <CustomerDetailsModal 
        userId={selectedCustomerId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <VendorDetailsModal 
        vendorId={selectedVendorId}
        isOpen={isVendorModalOpen}
        onClose={() => setIsVendorModalOpen(false)}
      />

      {/* Edit Address Modal */}
      {isAddressModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in border border-stone-200">
            <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-3">
              <h3 className="font-bold text-stone-800 text-sm flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[18px] text-stone-600 font-bold">edit_road</span>
                Edit Shipping Address
              </h3>
              <button 
                onClick={() => setIsAddressModalOpen(false)}
                className="text-stone-400 hover:text-stone-600 font-bold text-xs"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSaveAddress} className="space-y-3.5">
              <div>
                <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">Receiver Name</label>
                <input 
                  type="text" 
                  required 
                  value={addressForm.name} 
                  onChange={e => setAddressForm(prev => ({...prev, name: e.target.value}))} 
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                />
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">Phone Number (10 digits starting with 6-9)</label>
                <input 
                  type="text" 
                  required 
                  value={addressForm.phone} 
                  onChange={e => setAddressForm(prev => ({...prev, phone: e.target.value}))} 
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">House / Flat No.</label>
                  <input 
                    type="text" 
                    required 
                    value={addressForm.houseNo} 
                    onChange={e => setAddressForm(prev => ({...prev, houseNo: e.target.value}))} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">Area / Street</label>
                  <input 
                    type="text" 
                    required 
                    value={addressForm.area} 
                    onChange={e => setAddressForm(prev => ({...prev, area: e.target.value}))} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">Landmark (Optional)</label>
                <input 
                  type="text" 
                  value={addressForm.landmark} 
                  onChange={e => setAddressForm(prev => ({...prev, landmark: e.target.value}))} 
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">City</label>
                  <input 
                    type="text" 
                    required 
                    value={addressForm.city} 
                    onChange={e => setAddressForm(prev => ({...prev, city: e.target.value}))} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">State</label>
                  <input 
                    type="text" 
                    required 
                    value={addressForm.state} 
                    onChange={e => setAddressForm(prev => ({...prev, state: e.target.value}))} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] font-black uppercase text-stone-400 mb-1">Pincode</label>
                  <input 
                    type="text" 
                    required 
                    value={addressForm.pincode} 
                    onChange={e => setAddressForm(prev => ({...prev, pincode: e.target.value}))} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#201D42] font-medium"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-3 border-t border-stone-100 justify-end">
                <button 
                  type="button" 
                  onClick={() => setIsAddressModalOpen(false)}
                  className="bg-stone-100 text-stone-600 px-4 py-2 rounded-lg font-bold text-xs hover:bg-stone-200 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="bg-[#201D42] text-white px-5 py-2 rounded-lg font-bold text-xs hover:bg-black transition-all shadow-md"
                >
                  Save Address
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;